import { inject, Injectable } from '@angular/core';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { Observable, map } from 'rxjs';
import {
  DealProduct,
  ModelElevationProductApiModel,
  ProductAttributeGroup,
  ProductFilters,
  ProductLocationAssociation,
  ProductOption
} from '../models/deal-options.models';

@Injectable({
  providedIn: 'root'
})
export class ModelElevationProductsService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  getModelElevationProducts(
    modelId: string,
    elevationId: string,
    filters?: ProductFilters
  ): Observable<ProductOption[]> {
    const endpoint = this.#buildEndpoint(modelId, elevationId, filters);

    return this.#api
      .getData<
        ModelElevationProductApiModel[]
      >(`${this.baseUrl}/modelelevationproducts/${endpoint}`, { showLoader: true, showMessage: false })
      .pipe(map(products => products.map((api: ModelElevationProductApiModel) => this.#mapApiToProduct(api))));
  }

  #buildEndpoint(modelId: string, elevationId: string, filters?: ProductFilters): string {
    const queryParams = this.#buildQueryParams(filters);
    return `models/${modelId}/elevations/${elevationId}${queryParams ? `?${queryParams}` : ''}`;
  }

  #buildQueryParams(filters?: ProductFilters): string {
    if (!filters) return '';

    const params = new URLSearchParams();
    const paramMap: Record<string, string | boolean | null | undefined> = {
      RoomId: filters.roomId,
      CategoryId: filters.categoryId,
      SubcategoryId: filters.subcategoryId,
      OptionTypeId: filters.optionTypeId
    };

    Object.entries(paramMap).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });

    if (filters.applyCutoff === true) {
      params.set('ApplyCutoff', 'true');
    }

    return params.toString();
  }

  #mapApiToProduct(api: ModelElevationProductApiModel): ProductOption {
    const DEFAULT_VALUE = '—';

    return {
      modelElevationProductsId: api.id,
      productId: api.product?.id ?? api.productId,
      code: api.product?.code ?? DEFAULT_VALUE,
      description: api.product?.description ?? DEFAULT_VALUE,
      salesDescription: api.product?.salesDescription ?? DEFAULT_VALUE,
      type: api.productType?.name ?? api.product?.productClass?.name ?? DEFAULT_VALUE,
      uom: api.product?.uofm?.code ?? api.product?.uofm?.description ?? DEFAULT_VALUE,
      unitPrice: api.unitPrice ?? 0,
      selectionRequired: api.product?.selectionRequired ?? false,
      category: api.product?.subcategory?.category?.code ?? DEFAULT_VALUE,
      subcategory: api.product?.subcategory?.code ?? DEFAULT_VALUE,
      images: api.product?.images ?? []
    };
  }

  getProductAttributes(productId: string, groupByAttribute = true): Observable<ProductAttributeGroup[]> {
    const query = groupByAttribute ? '?groupByAttribute=true' : '';
    return this.#api.getData<ProductAttributeGroup[]>(`${this.baseUrl}/products/${productId}/attributes${query}`, {
      showLoader: false,
      showMessage: false
    });
  }

  getProductLocations(addendumId: string, productId: string): Observable<ProductLocationAssociation[]> {
    return this.#api.getData<ProductLocationAssociation[]>(
      `${this.baseUrl}/addendums/${addendumId}/locations?productId=${productId}`,
      {
        showLoader: false,
        showMessage: false
      }
    );
  }

  getProductLocationsByProductId(productId: string): Observable<ProductLocationAssociation[]> {
    return this.#api
      .getData<
        { id: string; locationId: string; location: { locationCode: string; locationName: string } }[]
      >(`${this.baseUrl}/products/${productId}/locations`, { showLoader: false, showMessage: false })
      .pipe(
        map(items =>
          (items ?? []).map(
            (item: { id: string; locationId: string; location: { locationCode: string; locationName: string } }) => ({
              id: item.locationId,
              locationCode: item.location.locationCode,
              locationName: item.location.locationName
            })
          )
        )
      );
  }

  getModelElevationProductById(modelElevationProductId: string): Observable<ModelElevationProductApiModel | null> {
    return this.#api
      .getData<ModelElevationProductApiModel>(`${this.baseUrl}/modelelevationproducts/${modelElevationProductId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(map(product => product ?? null));
  }

  getAddendumOptionDetailsById(modelElevationProductId: string): Observable<DealProduct | null> {
    return this.#api
      .getData<DealProduct>(`${this.baseUrl}/addendum-products/${modelElevationProductId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(map(product => product ?? null));
  }
}
